// frontend/src/modules/admin/components/ModeTestToggle.jsx
// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT MODE TEST WORKFLOW - Farm-Booking
// ═══════════════════════════════════════════════════════════════════════════
// Papy 73+ (Marine marchande, Normandie)
// Permet d'activer/désactiver le mode test + vider les réservations
// ═══════════════════════════════════════════════════════════════════════════
// VERSION CORRIGÉE : reservations_v2 → reservations
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { AlertTriangle, Info, Trash2, RefreshCw } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const ModeTestToggle = ({ config, onConfigUpdate }) => {
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showConfirmPurge, setShowConfirmPurge] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeStats, setPurgeStats] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // TOGGLE MODE TEST
  // ═══════════════════════════════════════════════════════════════════════════
  const handleToggle = async (checked) => {
    setSaving(true);
    setMessage('');
    
    try {
      const newValue = checked ? 'oui' : 'non';
      
      // Route PUT /api/configuration/:param_name
      const response = await fetch(`${API_BASE}/configuration/mode_test`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ param_value: newValue })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
      }
      
      setMessage(checked ? '✅ Mode test activé' : '✅ Mode test désactivé');
      setTimeout(() => setMessage(''), 3000);
      
      // Recharger la config parent
      if (onConfigUpdate) {
        onConfigUpdate();
      }
      
    } catch (error) {
      console.error('Erreur toggle mode test:', error);
      setMessage(`❌ Erreur: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // VIDER TOUTES LES RÉSERVATIONS (PURGE)
  // ═══════════════════════════════════════════════════════════════════════════
  const handlePurgeReservations = async () => {
    setPurging(true);
    setMessage('');
    
    try {
      const response = await fetch(`${API_BASE}/reservations/purge-all`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || result.message || 'Erreur lors de la purge');
      }
      
      // ⚠️ Gestion robuste des stats (peut être undefined)
      const stats = result.stats || { reservations: 0, lignes: 0, logs: 0 };
      
      setPurgeStats(stats);
      setMessage(`✅ Purge effectuée : ${stats.reservations || 0} réservations et ${stats.lignes || 0} lignes supprimées`);
      setShowConfirmPurge(false);
      
      // Recharger après 2 secondes
      setTimeout(() => {
        if (onConfigUpdate) onConfigUpdate();
        setPurgeStats(null);
      }, 3000);
      
    } catch (error) {
      console.error('Erreur purge réservations:', error);
      setMessage(`❌ Erreur purge: ${error.message}`);
      setShowConfirmPurge(false);
    } finally {
      setPurging(false);
    }
  };

  const isActive = config?.mode_test === 'oui';

  return (
    <div className={`rounded-lg shadow-md overflow-hidden ${
      isActive ? 'bg-yellow-50 border-2 border-yellow-400' : 'bg-white border border-gray-200'
    }`}>
      <div className="p-6">
        
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* EN-TÊTE AVEC TOGGLE */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className={`h-6 w-6 ${isActive ? 'text-yellow-600 animate-pulse' : 'text-gray-400'}`} />
              <h3 className={`text-lg font-bold ${isActive ? 'text-yellow-900' : 'text-gray-900'}`}>
                {isActive ? '⚡ Mode Test ACTIF' : 'Mode Test Workflow'}
              </h3>
            </div>
            
            <p className={`text-sm ${isActive ? 'text-yellow-800' : 'text-gray-600'}`}>
              {isActive 
                ? 'Toutes les contraintes de workflow sont désactivées'
                : 'Activer pour bypasser les contraintes de dates/heures'
              }
            </p>
          </div>
          
          {/* Toggle Switch */}
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={saving}
              className="sr-only peer"
            />
            <div className={`w-16 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all ${
              saving ? 'opacity-50 cursor-wait' : ''
            } peer-checked:bg-yellow-500`}></div>
          </label>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* MESSAGE DE CONFIRMATION */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {message && (
          <div className={`p-3 rounded-lg mb-4 text-sm font-medium ${
            message.includes('✅') 
              ? 'bg-green-100 text-green-800 border border-green-300' 
              : 'bg-red-100 text-red-800 border border-red-300'
          }`}>
            {message}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* CONTENU QUAND MODE ACTIF */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {isActive ? (
          <div className="space-y-4">
            
            {/* Modifications actives */}
            <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Modifications actives :
              </h4>
              <ul className="text-sm text-yellow-800 space-y-1.5">
                <li>✅ Créer des réservations hors périodes normales</li>
                <li>✅ Modifier/confirmer réservations à tout moment</li>
                <li>✅ Pas de blocage par jour/heure du workflow</li>
                <li>✅ Toutes les transitions de statuts autorisées</li>
                <li>✅ Bandeau jaune visible sur toutes les pages</li>
              </ul>
            </div>

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* BOUTON VIDER LES RÉSERVATIONS */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                Nettoyage des données de test
              </h4>
              <p className="text-sm text-red-700 mb-3">
                Supprime TOUTES les réservations et leurs lignes. Action irréversible !
              </p>
              
              {!showConfirmPurge ? (
                <button
                  onClick={() => setShowConfirmPurge(true)}
                  className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-medium transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Vider toutes les réservations
                </button>
              ) : (
                <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                  <p className="text-red-900 font-bold mb-3">
                    ⚠️ ATTENTION : Cette action est IRRÉVERSIBLE !
                  </p>
                  <p className="text-red-800 text-sm mb-4">
                    Toutes les réservations et leurs lignes produits 
                    seront définitivement supprimées.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowConfirmPurge(false)}
                      disabled={purging}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handlePurgeReservations}
                      disabled={purging}
                      className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
                    >
                      {purging ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Purge en cours...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4" />
                          Confirmer la purge
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Stats après purge */}
              {purgeStats && (
                <div className="mt-3 bg-green-100 border border-green-300 rounded-lg p-3 text-sm text-green-800">
                  <strong>✅ Purge effectuée :</strong>
                  <ul className="mt-1 ml-4">
                    <li>• {purgeStats.reservations} réservation(s) supprimée(s)</li>
                    <li>• {purgeStats.lignes} ligne(s) supprimée(s)</li>
                    <li>• {purgeStats.logs} log(s) email supprimé(s)</li>
                  </ul>
                </div>
              )}
            </div>

            {/* Avertissement */}
            <div className="pt-3 border-t border-yellow-300">
              <p className="text-xs text-yellow-700 flex items-start gap-2">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Attention :</strong> Le bandeau jaune est visible par tous les utilisateurs 
                  (clients et admin). Pensez à désactiver ce mode en production normale.
                </span>
              </p>
            </div>
          </div>
        ) : (
          /* ═══════════════════════════════════════════════════════════════════ */
          /* CONTENU QUAND MODE INACTIF */
          /* ═══════════════════════════════════════════════════════════════════ */
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Utilité du mode test :</h4>
            <ul className="text-sm text-gray-700 space-y-1.5">
              <li>• Tester le workflow sans attendre les bonnes dates/heures</li>
              <li>• Former des utilisateurs sans contraintes temporelles</li>
              <li>• Déboguer des problèmes de statuts</li>
              <li>• Simuler rapidement un cycle hebdomadaire complet</li>
            </ul>
            
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                💡 Activez ce mode temporairement pour vos tests, puis désactivez-le pour revenir 
                au fonctionnement normal avec contraintes.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModeTestToggle;